// chart-config.service.ts — THEME-AWARE version
// Replaces: src/app/shared/charts/services/chart-config.service.ts
//
// Change: all text/grid colors are resolved from the active theme at call
// time (body.theme-light), instead of hardcoded dark values. Legend, axis
// labels, and grid lines become readable in light mode.
import { Injectable } from '@angular/core';
import * as Highcharts from 'highcharts';
import { ChartThemeService } from './chart-theme.service';

interface ChartUiColors {
  text: string;
  textHover: string;
  textHidden: string;
  grid: string;
  axisLine: string;
  background: string;
}

const CHART_UI_DARK: ChartUiColors = {
  text: '#E0E0E3',
  textHover: '#FFFFFF',
  textHidden: '#606063',
  grid: 'rgba(255, 255, 255, 0.1)',
  axisLine: '#707073',
  background: '#323232',
};

const CHART_UI_LIGHT: ChartUiColors = {
  text: '#3d4a46',
  textHover: '#111816',
  textHidden: '#b3bdba',
  grid: 'rgba(0, 0, 0, 0.08)',
  axisLine: '#c2ccc9',
  background: '#f7f9f8',
};

@Injectable({
  providedIn: 'root',
})
export class ChartConfigService {
  constructor(private themeService: ChartThemeService) {}

  /** Resolved at call time so charts created after a theme switch pick it up. */
  private get ui(): ChartUiColors {
    return document.body.classList.contains('theme-light')
      ? CHART_UI_LIGHT
      : CHART_UI_DARK;
  }

  getLineChartConfig(): Highcharts.Options {
    const ui = this.ui;
    return {
      chart: {
        type: 'line',
        zooming: {
          type: 'x',
        },
        backgroundColor: ui.background,
        borderRadius: 5,
      },
      title: {
        text: undefined,
        style: {
          color: ui.text,
        },
      },
      credits: { enabled: false },
      colors: this.themeService.getCurrentPalette(),
      xAxis: {
        type: 'datetime',
        lineColor: ui.axisLine,
        tickColor: ui.axisLine,
        labels: {
          style: {
            color: ui.text,
          },
        },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: ui.grid,
        labels: {
          style: {
            color: ui.text,
          },
        },
      },
      tooltip: {
        shared: true,
        valueDecimals: 2,
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
        },
      },
      legend: {
        enabled: true,
        align: 'right',
        verticalAlign: 'top',
        borderWidth: 0,
        itemStyle: {
          color: ui.text,
        },
        itemHoverStyle: {
          color: ui.textHover,
        },
        itemHiddenStyle: {
          color: ui.textHidden,
        },
      },
      exporting: {
        enabled: false,
      },
    };
  }

  // Other chart type configurations...
}
